import React, { useCallback, useContext, useState } from 'react';
import { Button } from '@material-ui/core';
import { format } from 'date-fns';
import { ETables } from '../../types/enums/ETables';
import useCellStatusStyleHandler from '../../utils/useCellStatusStyleHandler';
import useColumnsHeaders from '../../utils/useColumnsHeaders';
import { NSCruiseLines } from '../../types/interfaces/NSCruiseLines';
import { SessionContext } from '../../contexts/session/SessionContext';
import { useTranslation } from 'react-i18next';
import tPaths from '../../utils/tPaths';
import useApi from '../../utils/useApi';
import { endpoints } from '../../utils/endpoints';
import useSnackbar from '../../utils/useSnackbar';
import CruiseShipsTableWithState from '../CruiseShips/CruiseShipsTableWithState';
import AddCruiseLineDialog from './components/AddCruiseLineDialog';
import { EStatus } from '../../types/enums/EStatus';
import { EPaths } from '../../types/enums/EPaths';

const tableName = ETables.CruiseLines;

const formatEditedDate = (date?: string) => (date ? format(new Date(date), 'dd.MM.yyyy HH:mm') : '-');

const CruiseLines = () => {
  const cellStyleHandler = useCellStatusStyleHandler();
  const { permissions } = useContext(SessionContext);
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { patch } = useApi(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleDeactivate = useCallback(
    (id: string, event: React.MouseEvent) => {
      event.stopPropagation();
      patch({
        endpoint: endpoints.cruiseLine(id),
        body: { status: 'Inactive' },
        onSuccess: () => {
          snackbar({ severity: 'success', message: t(tPaths.table.successOnDeactivateCruiseLine) });
          setRefreshKey((k) => k + 1);
        },
      });
    },
    [patch, snackbar, t],
  );

  const dataFormatter = useCallback(
    (dataTable: NSCruiseLines.ITableItem[]): NSCruiseLines.IItemDataFormatted[] =>
      dataTable.map((table) => {
        const statusValue = table.status?.current || '';
        const isActive = statusValue === EStatus.Active;

        return {
          _id: table._id,
          name: table.name,
          shipsCount: table.shipsCount ?? 0,
          status: statusValue,
          edited: formatEditedDate(table.status?.changedAt),
          action: () =>
            isActive && permissions.packages.edit ? (
              <Button size="small" color="secondary" onClick={(e) => handleDeactivate(table._id, e)}>
                {t(tPaths.table.deactivate)}
              </Button>
            ) : null,
        };
      }),
    [handleDeactivate, permissions.packages.edit, t],
  );

  const columns = useColumnsHeaders([
    { accessorAsHeader: 'name' },
    { accessorAsHeader: 'shipsCount' },
    { accessorAsHeader: 'status' },
    { accessorAsHeader: 'edited' },
    { accessorAsHeader: 'action' },
  ]);

  return (
    <>
      <CruiseShipsTableWithState
        tableName={tableName}
        dataFormatter={dataFormatter}
        cellStyleHandler={cellStyleHandler}
        columns={columns}
        exportable={true}
        refreshable={true}
        addable={permissions.packages.edit}
        onAddClick={() => setIsAddDialogOpen(true)}
        refreshTrigger={refreshKey}
        listPath={EPaths.CruiseLines}
        addButtonLabelKey={tPaths.table.addCruiseLine}
        exportFileName="cruise-lines"
      />
      <AddCruiseLineDialog
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onSuccess={() => setRefreshKey((k) => k + 1)}
      />
    </>
  );
};

export default CruiseLines;
